// SessionProvider — on mount, calls GET /api/session/me and exposes the
// result via React context. Handles the R5.1 redirect-on-401 contract and
// the R5.3 single-role auto-set.
//
// State management choice: React Context. A single pub/sub for a tiny
// number of consumers (topbar role switcher, Dashboard grants list) does
// not warrant zustand/jotai. React Query would pay off once we add
// refetch-on-role-switch in task 42.3 — at that point it's a strict
// superset of the current contract and we can layer it without breaking
// consumers.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApiError,
  api,
  clearCurrentRole,
} from "@/api";
import { _setLatchedSession } from "@/role/capabilities";
import type { SessionMe } from "./types";
import {
  SessionContext,
  type SessionContextValue,
  type SessionStatus,
} from "./context";

/**
 * Routes that must NOT trigger a /api/session/me fetch on mount because they
 * run before a full session exists. Keeping this as a Set (rather than the
 * former two-way pathname equality) makes adding pre-session routes a one-line
 * change and keeps the allowlist auditable in one place.
 */
const PRE_SESSION_PATHS = new Set<string>([
  "/signin",
  "/tenant-picker",
  "/link",
  "/link-error",
  "/workspace-ready",
]);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [session, setSession] = useState<SessionMe | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const load = useCallback(async (): Promise<void> => {
    // Pre-session routes: do NOT fetch /api/session/me here — otherwise its
    // 401 redirects to /signin and kicks the user off the page before it can
    // run its own pre-session flow. These pages are scoped to a pre-session
    // cookie (or no cookie at all, for the Ring-initiated install form), not a
    // full session; SessionContext stays unauthenticated until activation.
    //   /signin, /tenant-picker        — login + multi-tenant chooser
    //   /link, /link-error             — Ring App Store install form + errors
    //   /workspace-ready               — post-link landing (polls /api/tenant)
    if (PRE_SESSION_PATHS.has(location.pathname)) {
      setStatus("unauthenticated");
      setSession(null);
      return;
    }

    setStatus("loading");
    setError(null);
    try {
      // skipActingRole: /api/session/me is the one /api/* endpoint that does
      // not require a role header (LLD §6.4, R3.3).
      // skipAuthRedirect: we want to handle the 401 ourselves here, not let
      // the fetch wrapper trigger a navigation under us.
      const me = await api.get<SessionMe>("/api/session/me", {
        skipActingRole: true,
        skipAuthRedirect: true,
      });
      setSession(me);
      _setLatchedSession(me);
      setStatus("authenticated");

      // Role resolution (42.1 auto-select, 42.2 picker, 42.4 ?as=) lives in
      // RoleProvider so there is exactly one authority for sessionStorage.
      // currentRole. Do NOT set it here.
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // R5.1: 401 -> /signin. Clear any stale role so the picker isn't
        // fed a value from a previous session.
        clearCurrentRole();
        setSession(null);
        _setLatchedSession(null);
        setStatus("unauthenticated");
        navigate("/signin", { replace: true });
        return;
      }
      const apiErr =
        err instanceof ApiError
          ? err
          : new ApiError(0, "unknown", String(err), undefined, null);
      setError(apiErr);
      setStatus("error");
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    void load();
    // Intentionally mount-only; caller uses `refresh` for re-fetch. The
    // route-level rerun would cause spurious /api/session/me calls on
    // every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, session, error, refresh: load }),
    [status, session, error, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
